package it.maicol07.gamerlogue.services.connectors

import it.maicol07.gamerlogue.services.EpicApi
import it.maicol07.gamerlogue.services.ExternalService
import it.maicol07.gamerlogue.services.ServiceConnector
import it.maicol07.gamerlogue.services.ServiceProfile
import it.maicol07.gamerlogue.services.SyncScripts
import it.maicol07.gamerlogue.services.WebStep
import it.maicol07.gamerlogue.services.WishlistWrite
import it.maicol07.gamerlogue.services.apiProfile
import it.maicol07.gamerlogue.services.apiRefs
import it.maicol07.gamerlogue.services.webRefs

/**
 * Epic Games Store.
 *
 * Epic redirects its landing pages across subdomains (www → store, account → accounts), so any
 * cross-origin `fetch` from an injected script hits CORS. Profile and owned games use the API path
 * (like PSN/Xbox): the WebView only navigates to the `id/api/redirect` JSON endpoint to grab a launcher
 * authorization code, then [EpicApi] exchanges it and reads the identity + library from Kotlin
 * (off-WebView, no CORS); the token response already carries the displayName, so the profile needs no
 * extra call. The wishlist is store-only (not reachable with the launcher token), so it's read via the
 * store's GraphQL POSTed same-origin from a store page, and written per-game by opening each product
 * page and clicking its bookmark button (like PSN).
 */
class EpicConnector(private val api: EpicApi) :
    ServiceConnector(ExternalService.EPIC, host = "epicgames.com", externalGameSource = 26) {
    override val loginUrl = "https://www.epicgames.com/id/login"

    override val sessionUrls = listOf("https://www.epicgames.com/", "https://store.epicgames.com/")

    // When an id.epicgames.com session already exists, the login page shows an account picker with a
    // "Continue with my account" button instead of the credential form. Auto-click it so the user isn't
    // prompted every time. Text match on "continue" covers the localized label (e.g., IT "Continua").
    // Fire-and-forget + no-op when the button is absent (real first login), so it's safe on any load.
    override val loginTriggerScript = SyncScripts.clickWhenPresent(
        label = "epic continue",
        finder = """
            function() {
                return Array.prototype.find.call(
                    document.querySelectorAll('button, a'),
                    function(e) { return /continu/i.test(e.textContent || ''); }
                );
            }
        """.trimIndent(),
    )

    // Recognise an Epic store product page (returns its slug) so IGDB `websites` URLs are matched for push.
    override fun uidFromUrl(url: String) =
        Regex("epicgames\\.com/(?:store/)?(?:[a-z]{2}-[A-Z]{2}/)?(?:p|product)/([^/?#]+)")
            .find(url)?.groupValues?.get(1)

    // Strip the `/xx-XX` locale segment so the store redirects to the user's own region.
    override fun normalizePushUrl(url: String) =
        url.replace(Regex("(store\\.epicgames\\.com)/[a-z]{2}-[A-Z]{2}/"), "$1/")

    // Navigate straight to the JSON `id/api/redirect` endpoint and read the code from the body — a
    // `fetch` from any Epic page is cross-origin (CORS-blocked), but a direct navigation is same-origin
    // with the www session cookies. Falls back to a `code=` query param if the endpoint 302-redirects.
    private val credentialStep = WebStep(
        "https://www.epicgames.com/id/api/redirect?clientId=$LauncherClientId&responseType=code",
        SyncScripts.wrap(
            """
            let code = '';
            try {
                let j = JSON.parse(document.body.innerText || '{}');
                code = j.authorizationCode || '';
            } catch (e) {}
            if (!code) code = (location.href.match(/code=([^&]+)/) || [])[1] || '';
            console.log('[GL] epic code=' + (code ? 'ok' : 'empty'));
            out = code;
            """.trimIndent(),
        ),
    )

    // Owned games via the launcher library (the credential is a launcher authorization code).
    override val ownedGames = apiRefs(credentialStep) { code -> api.ownedGames(api.token(code).accessToken) }

    // The launcher token response includes displayName + account_id, so the profile needs no extra call.
    // Epic's launcher API exposes no avatar URL; profileUrl points at the public store profile page.
    override val profile = apiProfile(credentialStep) { code ->
        val token = api.token(code)
        token.displayName.takeIf { it.isNotBlank() }?.let { name ->
            ServiceProfile(
                username = name,
                avatarUrl = null,
                profileUrl = token.accountId.takeIf { it.isNotBlank() }
                    ?.let { "https://store.epicgames.com/u/$it" },
            )
        }
    }

    // Wishlist read via the store's own GraphQL, POSTed same-origin from a store page (so no CORS and no
    // dependence on the off-screen DOM rendering). The launcher token can't reach it — the wishlist is a
    // store concept — but the store session cookie can. Logs any GraphQL errors (e.g. persisted-query
    // enforcement) for tuning. uid = offerId (name drives the IGDB match, as Epic has no URL template).
    override val wishlist = webRefs(
        WebStep(
            "https://store.epicgames.com/",
            SyncScripts.wrap(
                """
                let q = 'query wishlistQuery { Wishlist { wishlistItems { elements { offerId namespace offer { title productSlug } } } } }';
                let r = await fetch('https://store.epicgames.com/graphql', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ query: q, operationName: 'wishlistQuery' }),
                });
                let j = await r.json();
                console.log('[GL] epic wishlist status=' + r.status + ' err=' + (j.errors ? JSON.stringify(j.errors).slice(0, 200) : 'none'));
                let els = (((j.data || {}).Wishlist || {}).wishlistItems || {}).elements || [];
                let byUid = {};
                els.forEach(function(e) {
                    let offer = e.offer || {};
                    let uid = e.offerId || offer.productSlug || '';
                    let name = offer.title || '';
                    if (uid && name) byUid[uid] = { uid: String(uid), name: name };
                });
                out = Object.values(byUid);
                console.log('[GL] epic wishlist games=' + out.length);
                """.trimIndent(),
            ),
        ),
    )

    // Push per game: open the product page and click its bookmark button. The button's icon carries a
    // language-independent data-testid — "empty-icon" (not wishlisted) / "filled-icon" (already added) —
    // so we detect the state without reading the localized label.
    override val wishlistWrite = WishlistWrite.PerGame { storeUrl ->
        WebStep(
            storeUrl,
            SyncScripts.wrap(
                """
                let btnWith = function(testid) {
                    return Array.prototype.find.call(document.querySelectorAll('button'), function(b) {
                        return b.querySelector('svg[data-testid="' + testid + '"]') && b.offsetParent !== null;
                    });
                };
                let ok = await __glClickUntil(
                    function() { return btnWith('empty-icon'); },
                    function() { return !!btnWith('filled-icon'); },
                    6
                );
                out = ok ? [{ uid: '$storeUrl', name: 'added' }] : [];
                console.log('[GL] epic wishlist push added=' + ok + ' ' + location.href);
                """.trimIndent(),
            ),
        )
    }

    private companion object {
        // Public "fortnitePCGameClient" launcher client id — used to request the authorization code.
        const val LauncherClientId = "34a02cf8f4414e29b15921876da36f9a"
    }
}
